import { useDepartmentText } from 'model/DepartmentTerminology';
import { ADD_ORGANIZATION_PAGE_PATH } from 'routes/paths';
import { useApi } from 'api/api-context';
import DataTable from 'components/tables/DataTable';
import QualificationsSelectFilter from 'components/tables/filters/QualificationsSelectFilter';
import { useCallback, useMemo } from 'react';
import { getDeviceType } from 'model/DeviceType';
import { Organization, OrganizationRow } from 'components/tables/data/organization/types';
import OrganizationIdColumn from 'components/tables/data/organization/columns/OrganizationIdColumn';
import OrganizationNameColumn from 'components/tables/data/organization/columns/OrganizationNameColumn';
import AddressColumn from 'components/tables/data/organization/columns/AddressColumn';
import RegistrationNumbersColumn from 'components/tables/data/organization/columns/RegistrationNumbersColumn';
import QualificationsColumn from 'components/tables/data/organization/columns/QualificationsColumn';
import DepartmentsColumn from 'components/tables/data/organization/columns/DepartmentsColumn';
import HierarchyColumn from 'components/tables/data/organization/columns/HierarchyColumn';
import ActionsColumn from 'components/tables/data/organization/columns/ActionsColumn';
import ModuleEnabledColumn from 'components/tables/data/organization/columns/ModuleEnabledColumn';
import YesNoSelectFilter from 'components/tables/filters/YesNoSelectFilter';
import HierarchySelectFilter from 'components/tables/filters/HierarchySelectFilter';
import { hiddenSearchableColumn as hiddenSearchable } from 'components/tables/utils/hidden-searchable-column';
import P2Regular from 'components/typography/P2Regular';
import { ColDef } from 'ag-grid-community';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

const PriceOffersEnabledFilter = YesNoSelectFilter.configure({ filterKey: 'organizationPriceOffersEnabled' });
const HrEnabledFilter = YesNoSelectFilter.configure({ filterKey: 'organizationHrEnabled' });
const RevisionsEnabledFilter = YesNoSelectFilter.configure({ filterKey: 'organizationRevisionsEnabled' });
const FormsEnabledFilter = YesNoSelectFilter.configure({ filterKey: 'organizationFormsEnabled' });
const RevisionTemplatesEnabledFilter = YesNoSelectFilter.configure({
  filterKey: 'organizationRevisionTemplatesEnabled',
});

/**
 * Plain-text organization official-name cell.
 */
function OfficialNameColumn({ value }: { value?: string }) {
  return <P2Regular color="gray.8">{value}</P2Regular>;
}

/**
 * Table which displays organizations.
 */
export default function OrganizationTable() {
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();

  const createRow = useCallback(
    (organization: Organization): OrganizationRow => ({
      ...organization,
      departmentCount: organization.departments.length,
      hierarchyName: organization.hierarchyAssignment.hierarchyName,
      qualificationNames: organization.qualifications
        .map(({ deviceType, deviceTypeId }) => deviceType?.deviceTypeName || getDeviceType(deviceTypeId).name)
        .join(','),
      qualificationSlugs: organization.qualifications
        .map(({ deviceType, deviceTypeId }) => deviceType?.slug || getDeviceType(deviceTypeId).slug)
        .join(','),
      organizationIncludedInStatistics: organization.includedInStatistics ? 'Áno' : 'Nie',
      organizationInKepHashArchive: organization.inKepHashArchive ? 'Áno' : 'Nie',
      organizationPriceOffersEnabled: organization.priceOffersEnabled ? 'Áno' : 'Nie',
      organizationHrEnabled: organization.hrEnabled ? 'Áno' : 'Nie',
      organizationRevisionsEnabled: organization.revisionsEnabled ? 'Áno' : 'Nie',
      organizationFormsEnabled: organization.formsEnabled ? 'Áno' : 'Nie',
      organizationRevisionTemplatesEnabled: organization.revisionTemplatesEnabled ? 'Áno' : 'Nie',
    }),
    []
  );

  const action = useCallback(async () => {
    const getOrganizations = getAction('OrganizationList');
    const organizations = await getOrganizations();
    return organizations.map(createRow);
  }, [getAction]);

  const dataExport = useMemo(
    () => ({
      modalTitle: 'Exportovať organizácie',
      fileName: 'organizacie.xlsx',
      columnReplacements: { address: ['streetAddress', 'city'], registrationNumbers: ['ico', 'dic', 'icdph'] },
      columnKeys: [
        'organizationId',
        'organizationName',
        'officialName',
        'departmentCount',
        'hierarchyName',
        'streetAddress',
        'city',
        'ico',
        'dic',
        'icdph',
        'organizationIncludedInStatistics',
        'organizationInKepHashArchive',
        'organizationPriceOffersEnabled',
        'organizationHrEnabled',
        'organizationRevisionsEnabled',
        'organizationFormsEnabled',
        'organizationRevisionTemplatesEnabled',
        'status',
      ],
    }),
    []
  );

  const columns = useMemo<ColDef[]>(
    () => [
      // ANCHOR Visible Columns
      {
        field: 'organizationId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        cellRenderer: OrganizationIdColumn,
      },
      {
        field: 'organizationName',
        headerName: 'Organizácia',
        minWidth: 350,
        sortable: true,
        filter: true,
        unSortIcon: true,
        cellRenderer: OrganizationNameColumn,
      },
      {
        field: 'officialName',
        headerName: 'Oficiálny názov',
        minWidth: 300,
        sortable: true,
        filter: true,
        unSortIcon: true,
        cellRenderer: OfficialNameColumn,
      },
      {
        field: 'address',
        headerName: 'Adresa',
        minWidth: 250,
        unSortIcon: true,
        cellRenderer: AddressColumn,
      },
      {
        field: 'registrationNumbers',
        headerName: 'IČO / DIČ / IČ DPH',
        minWidth: 200,

        cellRenderer: RegistrationNumbersColumn,
      },
      {
        field: 'qualifications',
        headerName: 'Oprávnenia',
        minWidth: 200,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: QualificationsSelectFilter,
        cellRenderer: QualificationsColumn,
      },
      {
        field: 'departmentCount',
        headerName: departmentText('Položky úrovne', 'Strediská'),
        minWidth: 120,
        sortable: true,
        unSortIcon: true,
        cellRenderer: DepartmentsColumn,
      },
      {
        field: 'hierarchyName',
        headerName: 'Hierarchia',
        minWidth: 220,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: HierarchySelectFilter,
        unSortIcon: true,
        cellRenderer: HierarchyColumn,
      },
      {
        field: 'organizationPriceOffersEnabled',
        headerName: 'Cenové ponuky',
        minWidth: 160,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: PriceOffersEnabledFilter,
        unSortIcon: true,
        cellRenderer: ModuleEnabledColumn,
      },
      {
        field: 'organizationHrEnabled',
        headerName: 'Ľudské zdroje',
        minWidth: 170,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: HrEnabledFilter,
        unSortIcon: true,
        cellRenderer: ModuleEnabledColumn,
      },
      {
        field: 'organizationRevisionsEnabled',
        headerName: 'Revízne správy',
        minWidth: 180,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: RevisionsEnabledFilter,
        unSortIcon: true,
        cellRenderer: ModuleEnabledColumn,
      },
      {
        field: 'organizationFormsEnabled',
        headerName: 'Formuláre',
        minWidth: 150,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: FormsEnabledFilter,
        unSortIcon: true,
        cellRenderer: ModuleEnabledColumn,
      },
      {
        field: 'organizationRevisionTemplatesEnabled',
        headerName: 'Šablóny revíznych správ',
        minWidth: 220,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: RevisionTemplatesEnabledFilter,
        unSortIcon: true,
        cellRenderer: ModuleEnabledColumn,
      },

      // ANCHOR Hidden Columns
      { field: 'streetAddress', headerName: 'Ulica', ...hiddenSearchable },
      { field: 'city', headerName: 'Mesto', ...hiddenSearchable },
      { field: 'ico', headerName: 'IČO', ...hiddenSearchable },
      { field: 'dic', headerName: 'DIČ', ...hiddenSearchable },
      { field: 'icdph', headerName: 'IČ DPH', ...hiddenSearchable },
      { field: 'organizationIncludedInStatistics', headerName: 'Rátať do štatistík', ...hiddenSearchable },
      { field: 'organizationInKepHashArchive', headerName: 'KEP hash archív', ...hiddenSearchable },
      { field: 'qualificationNames', headerName: '', ...hiddenSearchable },
      { field: 'qualificationSlugs', headerName: '', ...hiddenSearchable },

      // ANCHOR Status
      {
        field: 'status',
        headerName: 'Aktívna',
        valueGetter: ({ data: { status } }: { data: OrganizationRow }) => Number(status),
        hide: true,
        filter: true,
      },

      // ANCHOR Actions
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        lockPosition: 'right',
        cellRenderer: ActionsColumn,
      },
    ],
    [departmentText, tableActionMode]
  );

  return (
    <DataTable
      tableId="organizations"
      title="Organizácie"
      addButtonText="Pridať novú"
      toggleDiscardedLabel="Zobraziť vyradené"
      columns={columns}
      action={action}
      addButtonTarget={ADD_ORGANIZATION_PAGE_PATH.original}
      dataExport={dataExport}
    />
  );
}
